// src/store/dicom-web/orthanc-store.ts

import type { DICOMwebClient } from '@/src/core/dicom-web-api-new';
import type {
  OrthancAPIClient,
  OrthancChangeEntry,
  OrthancSeriesInfo,
  OrthancStatistics,
  OrthancStudyInfo,
} from '@/src/core/orthanc-api';
import type { DicomArchiveConfig } from '@/src/supabase';

import JSZip from 'jszip';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { createDICOMwebClient } from '@/src/core/dicom-web-api-new';
import { createOrthancClient } from '@/src/core/orthanc-api';
import { getDicomArchiveConfig } from '@/src/supabase';

// ── Public types ────────────────────────────────────────────────

export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'error';
export type OpState = 'active' | 'done' | 'error';

const STUDY_LIST_RESPONSE_CONTENT
  = 'MainDicomTags;Metadata;Children;Parent;Labels;Status;IsStable';

export interface OperationProgress {
  label: string;
  state: OpState;
  loaded: number;
  total: number;
  error?: string;
}

interface PaletteLutResult {
  red: Uint16Array | null;
  green: Uint16Array | null;
  blue: Uint16Array | null;
}

// ── Private helpers ─────────────────────────────────────────────

async function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
    reader.readAsArrayBuffer(file);
  });
}

function isZipMagic(buf: ArrayBuffer): boolean {
  if (buf.byteLength < 4) return false;
  const view = new DataView(buf);
  return view.getUint32(0) === 0x504B0304;
}

async function hasNestedZip(buf: ArrayBuffer): Promise<boolean> {
  try {
    const zip = await JSZip.loadAsync(buf);
    for (const file of Object.values(zip.files)) {
      if (file.dir) continue;
      if (file.name.toLowerCase().endsWith('.zip')) {
        return true;
      }
    }
  }
  catch {
    return false;
  }
  return false;
}

// async function fetchBulkDataAsUint16(
//   uri: string,
//   compress: boolean = false
// ): Promise<Uint16Array | null> {
//   try {
//     const url = compress ? `${uri}${uri.includes('?') ? '&' : '?'}compress=1` : uri;
//     const resp = await fetch(url);
//     if (!resp.ok) return null;
//     const buf = await resp.arrayBuffer();
//     return new Uint16Array(buf);
//   } catch {
//     return null;
//   }
// }

async function decompress(buf: ArrayBuffer): Promise<ArrayBuffer> {
  const ds = new DecompressionStream('gzip');
  const writer = ds.writable.getWriter();
  await writer.write(new Uint8Array(buf));
  await writer.close();

  const reader = ds.readable.getReader();
  const chunks: Uint8Array[] = [];
  // standard pattern for reading a ReadableStream to completion.
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }

  const total = chunks.reduce((sum, c) => sum + c.byteLength, 0);
  const result = new Uint8Array(total);
  let offset = 0;
  chunks.forEach((chunk) => {
    result.set(chunk, offset);
    offset += chunk.byteLength;
  });
  return result.buffer;
}

async function decompressNpz(buf: ArrayBuffer): Promise<ArrayBuffer> {
  const zip = await JSZip.loadAsync(buf);

  const files = Object.values(zip.files)
    .filter(f => !f.dir && /^arr_\d+$/.test(f.name))
    .sort((a, b) => a.name.localeCompare(b.name));

  if (files.length === 0)
    throw new Error('Invalid .npz: no array entries found');

  return files[0].async('arraybuffer');
}

// ── Store ───────────────────────────────────────────────────────

export const useOrthancStore = defineStore('orthanc-store', () => {
  // ==============================================================
  //  CONNECTION & CLIENTS
  //
  //  Connect-on-demand: every public method calls ensureConnected()
  //  first.  If the previous attempt failed, _connectPromise is
  //  cleared so the next call retries automatically.
  // ==============================================================

  const config = ref<DicomArchiveConfig | null>(null);
  const useCompression = ref(false);
  const connectionState = ref<ConnectionState>('idle');
  const connectionError = ref<string | null>(null);
  const isConnected = computed(() => connectionState.value === 'connected');

  let _orthanc: OrthancAPIClient | undefined;
  let _dicomWeb: DICOMwebClient | undefined;
  let _connectPromise: Promise<void> | undefined;

  async function connect() {
    if (_connectPromise) return _connectPromise;
    connectionState.value = 'connecting';
    connectionError.value = null;

    _connectPromise = (async () => {
      try {
        const configs = await getDicomArchiveConfig();
        const cfg = configs.at(0);
        if (!cfg) throw new Error('No Orthanc configuration found');
        cfg.url = cfg.url.replace(/\/$/, '');
        config.value = cfg;
        _orthanc = createOrthancClient(cfg);
        _dicomWeb = createDICOMwebClient(cfg);
        connectionState.value = 'connected';
      }
      catch (e) {
        connectionState.value = 'error';
        connectionError.value = e instanceof Error ? e.message : String(e);
        _orthanc = undefined;
        _dicomWeb = undefined;
        _connectPromise = undefined;
        throw e;
      }
    })();

    return _connectPromise;
  }

  async function ensureConnected(): Promise<void> {
    if (_orthanc && _dicomWeb) return;
    await connect();
  }

  function requireOrthanc(): OrthancAPIClient {
    if (!_orthanc) throw new Error('Not connected');
    return _orthanc;
  }

  function requireDicomWeb(): DICOMwebClient {
    if (!_dicomWeb) throw new Error('Not connected');
    return _dicomWeb;
  }

  async function getConfig(): Promise<DicomArchiveConfig> {
    await connect();
    if (!config.value) throw new Error('No Orthanc configuration available');
    return config.value;
  }

  // ==============================================================
  //  OPERATION PROGRESS
  // ==============================================================

  const operations = ref<Record<string, OperationProgress>>({});

  const activeOperations = computed(() =>
    Object.entries(operations.value)
      .filter(([, op]) => op.state === 'active')
      .map(([id, op]) => ({ id, ...op })),
  );

  function _startOp(id: string, label: string, total = 0): void {
    operations.value[id] = { label, state: 'active', loaded: 0, total };
  }
  function _updateOp(id: string, loaded: number, total?: number): void {
    const op = operations.value[id];
    if (!op) return;
    op.loaded = loaded;
    if (total !== undefined) op.total = total;
  }
  function _completeOp(id: string): void {
    const op = operations.value[id];
    if (!op) return;
    op.state = 'done';
    op.loaded = op.total;
  }
  function _failOp(id: string, error: string): void {
    const op = operations.value[id];
    if (!op) return;
    op.state = 'error';
    op.error = error;
  }
  function clearOperation(id: string): void {
    delete operations.value[id];
  }

  // ==============================================================
  //  ID LOOKUP MAPS
  //
  //  Bidirectional Orthanc ID ↔ DICOM UID maps, populated as a
  //  side effect of data already being fetched.
  // ==============================================================

  const studyIdByUID = ref<Map<string, string>>(new Map());
  const studyUIDById = ref<Map<string, string>>(new Map());
  const seriesIdByUID = ref<Map<string, string>>(new Map());
  const seriesUIDById = ref<Map<string, string>>(new Map());
  const instanceIdByUID = ref<Map<string, string>>(new Map());
  const instanceUIDById = ref<Map<string, string>>(new Map());

  function resolveStudyId(studyInstanceUID: string): string | undefined {
    return studyIdByUID.value.get(studyInstanceUID);
  }
  function resolveStudyUID(orthancId: string): string | undefined {
    return studyUIDById.value.get(orthancId);
  }
  function resolveSeriesId(seriesInstanceUID: string): string | undefined {
    return seriesIdByUID.value.get(seriesInstanceUID);
  }
  function resolveSeriesUID(orthancId: string): string | undefined {
    return seriesUIDById.value.get(orthancId);
  }
  function resolveInstanceId(
    sopInstanceUID: string | undefined,
  ): string | undefined {
    if (!sopInstanceUID) return undefined;
    const cleanSopUID = sopInstanceUID.split('-')[0];
    return instanceIdByUID.value.get(cleanSopUID);
  }
  function resolveInstanceUID(orthancId: string): string | undefined {
    return instanceUIDById.value.get(orthancId);
  }

  // ==============================================================
  //  SYSTEM STATISTICS
  //
  //  GET /statistics — single call, returns study/series/instance
  //  counts and disk usage.
  // ==============================================================

  const statistics = ref<OrthancStatistics | null>(null);
  async function fetchStatistics() {
    await ensureConnected();
    statistics.value = await requireOrthanc().getSystemStatistics();
    return statistics.value;
  }

  // ==============================================================
  //  LIST ALL STUDIES (MainDicomTags)
  //
  //  GET /studies?expand — single round-trip for all studies.
  // ==============================================================

  const studies = ref<OrthancStudyInfo[]>([]);

  async function fetchStudies(): Promise<OrthancStudyInfo[]> {
    await ensureConnected();
    const result = await requireOrthanc().listStudiesExpanded({
      responseContent: STUDY_LIST_RESPONSE_CONTENT,
    });
    // `null` means the request failed (timeout / non-2xx) — keep the current
    // list rather than blanking it (common right after a heavy upload). An
    // empty array is a valid "no studies" response and is applied normally.
    if (result == null) return studies.value;
    studies.value = result;
    studyIdByUID.value.clear();
    studyUIDById.value.clear();
    studies.value.forEach((s) => {
      studyIdByUID.value.set(s.MainDicomTags.StudyInstanceUID, s.ID);
      studyUIDById.value.set(s.ID, s.MainDicomTags.StudyInstanceUID);
    });
    void fetchStatistics();
    return studies.value;
  }

  let _studyPollTimer: ReturnType<typeof setInterval> | undefined;

  async function pollStudies() {
    try {
      await ensureConnected();
      const result = await requireOrthanc().listStudiesExpanded({
        responseContent: STUDY_LIST_RESPONSE_CONTENT,
      });
      // Skip on request failure (null) so a transient error/timeout doesn't
      // blank the list. An empty array is a valid, applied result.
      if (result == null) return;
      const incoming = result;

      // Compare by study ID (not array position) so a reordered list can't
      // mask a real status change.
      const prevById = new Map(studies.value.map(s => [s.ID, s]));
      const changed
        = incoming.length !== studies.value.length
          || incoming.some((s) => {
            const prev = prevById.get(s.ID);
            return (
              !prev
              || s.IsStable !== prev.IsStable
              || (s.LastUpdate ?? s.Metadata?.LastUpdate)
              !== (prev.LastUpdate ?? prev.Metadata?.LastUpdate)
              || s.Metadata?.OCG19_SegmentationStatus
              !== prev.Metadata?.OCG19_SegmentationStatus
              || s.Metadata?.OCG19_MetadataStatus
              !== prev.Metadata?.OCG19_MetadataStatus
            );
          });

      if (changed) {
        studies.value = incoming;
        studyIdByUID.value.clear();
        studyUIDById.value.clear();
        incoming.forEach((s) => {
          studyIdByUID.value.set(s.MainDicomTags.StudyInstanceUID, s.ID);
          studyUIDById.value.set(s.ID, s.MainDicomTags.StudyInstanceUID);
        });
        void fetchStatistics();
      }
    }
    catch {
      // swallow
    }
  }

  function stopStudyPolling() {
    if (_studyPollTimer) {
      clearInterval(_studyPollTimer);
      _studyPollTimer = undefined;
    }
  }

  function startStudyPolling(intervalMs = 10000) {
    stopStudyPolling();
    void pollStudies();
    _studyPollTimer = setInterval(() => void pollStudies(), intervalMs);
  }

  // ==============================================================
  //  LIST SERIES FOR A STUDY (MainDicomTags)
  //
  //  GET /studies/{id}/series?expand — single call returns all
  //  series with MainDicomTags.
  //  Accepts Orthanc study ID.
  // ==============================================================

  const studySeries = ref<OrthancSeriesInfo[]>([]);
  const selectedStudyOrthancID = ref<string | undefined>(undefined);

  async function fetchStudySeries(
    orthancStudyId: string,
  ): Promise<OrthancSeriesInfo[]> {
    await ensureConnected();
    const result
      = (await requireOrthanc().getStudySeriesExpanded(orthancStudyId)) ?? [];
    studySeries.value = result;
    selectedStudyOrthancID.value = orthancStudyId;
    seriesIdByUID.value.clear();
    seriesUIDById.value.clear();
    result.forEach((s) => {
      seriesIdByUID.value.set(s.MainDicomTags.SeriesInstanceUID, s.ID);
      seriesUIDById.value.set(s.ID, s.MainDicomTags.SeriesInstanceUID);
    });
    return result;
  }

  let _seriesPollTimer: ReturnType<typeof setInterval> | undefined;

  async function pollSeries() {
    try {
      await ensureConnected();
      const result = await requireOrthanc().getStudySeriesExpanded(
        selectedStudyOrthancID.value ?? '',
      );
      const incoming = result ?? [];
      const currentIds = studySeries.value.map(s => s.ID).join(',');
      const newIds = incoming.map(s => s.ID).join(',');
      if (currentIds !== newIds) {
        studySeries.value = incoming;
        seriesIdByUID.value.clear();
        seriesUIDById.value.clear();
        incoming.forEach((s) => {
          seriesIdByUID.value.set(s.MainDicomTags.SeriesInstanceUID, s.ID);
          seriesUIDById.value.set(s.ID, s.MainDicomTags.SeriesInstanceUID);
        });
      }
    }
    catch {
      // swallow
    }
  }

  function stopSeriesPolling() {
    if (_seriesPollTimer) {
      clearInterval(_seriesPollTimer);
      _seriesPollTimer = undefined;
    }
  }

  function startSeriesPolling(intervalMs = 10000) {
    stopSeriesPolling();
    void pollSeries();
    _seriesPollTimer = setInterval(() => void pollSeries(), intervalMs);
  }

  // ==============================================================
  //  OCG19 METADATA
  //
  //  Pre-built by the Orthanc Python plugin as a study attachment.
  //  Single GET, compact JSON array for all instances.
  //  Also populates the instance ID ↔ UID lookup maps.
  // ==============================================================

  async function fetchOcg19Metadata(orthancStudyId: string) {
    // if server responds with Content-Encoding: gzip, the browser’s fetch API decompresses the body
    const compressed = useCompression.value;
    await ensureConnected();
    const result = compressed
      ? await requireOrthanc().getStudyOcg19MetadataCompressed(orthancStudyId)
      : await requireOrthanc().getStudyOcg19Metadata(orthancStudyId);
    const arr = result ?? [];
    instanceIdByUID.value.clear();
    instanceUIDById.value.clear();
    arr.forEach((inst) => {
      if (inst.OrthancInstanceId && inst.SOPInstanceUID) {
        instanceIdByUID.value.set(inst.SOPInstanceUID, inst.OrthancInstanceId);
        instanceUIDById.value.set(inst.OrthancInstanceId, inst.SOPInstanceUID);
      }
    });
    return arr;
  }

  async function rebuildOcg19Metadata(orthancStudyId: string): Promise<any> {
    await ensureConnected();
    return requireOrthanc().rebuildStudyOcg19Metadata(orthancStudyId);
  }

  async function rebuildAllOcg19Metadata() {
    await ensureConnected();
    return requireOrthanc().rebuildAllOcg19Metadata();
  }

  // ==============================================================
  //  RAW FRAME DATA
  //
  //  Orthanc proprietary raw pixel retrieval with optional gzip
  //  compression.  Accepts Orthanc instance ID (from OCG19
  //  metadata _instanceId field).
  // ==============================================================

  async function fetchFrameRaw(
    orthancInstanceId: string,
    frame: number = 0,
  ): Promise<ArrayBuffer | undefined> {
    const compressed = useCompression.value;
    await ensureConnected();
    const client = requireOrthanc();

    const buf = compressed
      ? await client.getInstanceFrameRawGz(orthancInstanceId, frame)
      : await client.getInstanceFrameRaw(orthancInstanceId, frame);

    if (!buf || buf.byteLength === 0) return undefined;

    if (compressed) {
      return decompress(buf);
    }
    return buf;
  }

  // ==============================================================
  //  DICOMweb WADO-RS RAW FRAME DATA
  //
  //  DICOMweb WADO-RS frame retrieval with optional gzip
  //  compression via Accept-Encoding header.
  // ==============================================================

  async function fetchFrameRawDicomWeb(
    studyInstanceUID: string,
    seriesInstanceUID: string,
    sopInstanceUID: string,
    frame: number = 0,
  ): Promise<ArrayBuffer | undefined> {
    await ensureConnected();
    const client = requireDicomWeb();
    const compressed = useCompression.value;

    const frameNumber = String(frame + 1); // DICOMweb frames are 1-based
    const parts = await client.retrieveInstanceFrames(
      studyInstanceUID,
      seriesInstanceUID,
      sopInstanceUID,
      frameNumber,
      compressed,
    );

    const buf = parts?.[0];
    if (!buf || buf.byteLength === 0) return undefined;
    return buf;
  }

  // ==============================================================
  //  FRAME PIXEL DATA AS NUMPY
  //
  //  Orthanc decodes the frame and returns a numpy .npy (or .npz
  //  when compressed) binary blob.  With rescale=true, Orthanc
  //  applies RescaleSlope/Intercept server-side and returns
  //  floating-point values (e.g. Hounsfield units for CT).
  //  Accepts Orthanc instance ID (from OCG19 metadata _instanceId).
  // ==============================================================

  async function fetchFrameNumpy(
    orthancInstanceId: string,
    frame: number = 0,
    rescale: boolean = false,
  ): Promise<ArrayBuffer | undefined> {
    const compressed = useCompression.value;
    await ensureConnected();
    const client = requireOrthanc();

    const buf = await client.getInstanceFrameNumpy(orthancInstanceId, frame, {
      rescale,
      compress: compressed,
    });

    if (!buf || buf.byteLength === 0) return undefined;

    if (compressed) {
      return decompressNpz(buf);
    }
    return buf;
  }

  // ==============================================================
  //  DELETE STUDIES
  //
  //  Parallel deletion with progress tracking.
  // ==============================================================

  async function deleteStudies(orthancStudyIds: string[]): Promise<void> {
    await ensureConnected();
    const opId = `delete-studies-${Date.now()}`;
    _startOp(opId, 'Deleting studies', orthancStudyIds.length);
    const client = requireOrthanc();

    try {
      let done = 0;
      await Promise.all(
        orthancStudyIds.map(async (id) => {
          const uid = studyUIDById.value.get(id);
          await client.deleteStudy(id);
          if (uid) studyIdByUID.value.delete(uid);
          studyUIDById.value.delete(id);
          done++;
          _updateOp(opId, done);
        }),
      );
      _completeOp(opId);
      await fetchStudies();
    }
    catch (e) {
      _failOp(opId, e instanceof Error ? e.message : String(e));
      throw e;
    }
  }

  // ==============================================================
  //  UPLOAD DICOM FILES
  //
  //  Accepts a FileList.  Auto-detects ZIP vs raw DICOM via magic
  //  bytes.  Sequential to avoid overwhelming the server.
  // ==============================================================

  async function uploadFiles(files: FileList): Promise<void> {
    await ensureConnected();
    const opId = `upload-${Date.now()}`;
    const total = files.length;
    _startOp(opId, 'Uploading DICOM', total);
    const client = requireOrthanc();

    try {
      let done = 0;
      for (let i = 0; i < total; i++) {
        const file = files[i];
        const buf = await readFileAsArrayBuffer(file);
        const isZip
          = file.name.toLowerCase().endsWith('.zip') || isZipMagic(buf);

        if (isZip) {
          if (await hasNestedZip(buf)) {
            throw new Error(
              `File "${file.name}" contains nested ZIP archives. Nested ZIP files are not supported.`,
            );
          }
          await client.uploadInstancesZip(buf);
        }
        else {
          await client.uploadInstance(buf);
        }
        done++;
        _updateOp(opId, done);
      }
      _completeOp(opId);
      await fetchStudies();
    }
    catch (e) {
      _failOp(opId, e instanceof Error ? e.message : String(e));
      throw e;
    }
  }

  // ==============================================================
  //  PALETTE COLOR LUT DATA
  //
  //  Fetches R/G/B LUT channels in parallel
  //  Returns Uint16Arrays for direct use in rendering.
  //
  // ==============================================================

  async function fetchPaletteLut(
    orthancInstanceId: string,
  ): Promise<PaletteLutResult> {
    await ensureConnected();
    const client = requireOrthanc();
    const compressed = useCompression.value;

    const [red, green, blue] = await Promise.all([
      client.getInstanceContentBinary(
        orthancInstanceId,
        '0028-1201',
        compressed,
      ),
      client.getInstanceContentBinary(
        orthancInstanceId,
        '0028-1202',
        compressed,
      ),
      client.getInstanceContentBinary(
        orthancInstanceId,
        '0028-1203',
        compressed,
      ),
    ]);

    const result: PaletteLutResult = {
      red: red ? new Uint16Array(red) : null,
      green: green ? new Uint16Array(green) : null,
      blue: blue ? new Uint16Array(blue) : null,
    };
    return result;
  }

  // ==============================================================
  //  THUMBNAILS
  //
  //  Uses Orthanc proprietary rendered endpoint so window/level
  //  can be applied server-side.  Accepts Orthanc instance IDs
  //  (available from OCG19 metadata _instanceId field).
  // ==============================================================

  const THUMBNAIL_CONCURRENCY = 6;

  async function fetchThumbnail(
    orthancInstanceId: string,
    windowCenter?: number,
    windowWidth?: number,
    frame: number = 0,
  ): Promise<string | null> {
    await ensureConnected();
    const client = requireOrthanc();

    try {
      const data = await client.getInstanceFrameRendered(
        orthancInstanceId,
        frame,
        {
          windowCenter,
          windowWidth,
          quality: 70,
          width: 150,
          accept: 'image/jpeg',
        },
      );
      if (!data || data.byteLength === 0) return null;
      return URL.createObjectURL(new Blob([data], { type: 'image/jpeg' }));
    }
    catch {
      return null;
    }
  }

  async function fetchThumbnails(
    requests: Array<{
      orthancInstanceId: string;
      windowCenter?: number;
      windowWidth?: number;
      frame?: number;
    }>,
  ): Promise<(string | null)[]> {
    await ensureConnected();
    const opId = `thumbnails-${Date.now()}`;
    _startOp(opId, 'Loading thumbnails', requests.length);
    const results = Array.from<string | null>({
      length: requests.length,
    }).fill(null);

    let cursor = 0;
    let done = 0;

    async function worker() {
      while (cursor < requests.length) {
        const idx = cursor++;
        const r = requests[idx];
        results[idx] = await fetchThumbnail(
          r.orthancInstanceId,
          r.frame ?? 0,
          r.windowCenter,
          r.windowWidth,
        );
        done++;
        _updateOp(opId, done);
      }
    }

    const workers = Array.from(
      { length: Math.min(THUMBNAIL_CONCURRENCY, requests.length) },
      async () => worker(),
    );
    await Promise.all(workers);
    _completeOp(opId);

    return results;
  }

  // ==============================================================
  //  SYSTEM INFO (GET /system)
  // ==============================================================

  const systemInfo = ref<{ [key: string]: unknown } | null>(null);
  async function fetchSystemInfo() {
    await ensureConnected();
    const info = await requireOrthanc().getSystemInfo();
    systemInfo.value = info
      ? (info as unknown as { [key: string]: unknown })
      : null;
    return systemInfo.value;
  }

  // ==============================================================
  //  STATUS BAR
  //
  //  Reactive status message + Orthanc changes polling.
  // ==============================================================

  const statusMessage = ref('');
  const lastChange = ref<OrthancChangeEntry | null>(null);
  let _pollTimer: ReturnType<typeof setInterval> | undefined;
  let _consecutiveFailures = 0;

  function setStatusMessage(message: string) {
    statusMessage.value = message;
  }

  async function pollOrthancStatus() {
    try {
      await ensureConnected();
      const changes = await requireOrthanc().getChanges({ last: 0 });

      if (changes === null) {
        throw new Error('No Connection');
      }

      lastChange.value = changes.Changes?.[0] ?? null;

      if (connectionState.value !== 'connected') {
        connectionState.value = 'connected';
        connectionError.value = null;
      }
      _consecutiveFailures = 0;

      // Keep study metadata (e.g. OCG19_SegmentationStatus) fresh on the same
      // cadence as the status bar. pollStudies only reassigns on an actual
      // change, so this is cheap when nothing moved.
      void pollStudies();
    }
    catch (e) {
      _consecutiveFailures++;
      lastChange.value = null;

      if (_consecutiveFailures >= 1) {
        connectionState.value = 'error';
        connectionError.value
          = e instanceof Error ? e.message : 'Connection lost';
      }
    }
  }

  function stopStatusPolling() {
    if (_pollTimer) {
      clearInterval(_pollTimer);
      _pollTimer = undefined;
    }
    lastChange.value = null;
    _consecutiveFailures = 0;
  }

  function startStatusPolling(intervalMs = 5000) {
    stopStatusPolling();
    void pollOrthancStatus();
    _pollTimer = setInterval(() => void pollOrthancStatus(), intervalMs);
  }

  // ==============================================================
  //  GET INSTANCE SIMPLIFIED TAGS
  //
  //  GET /instances/{id}/simplified-tags — single call, returns
  //  simplified DICOM tags.
  // ==============================================================
  async function getInstanceSimplifiedTags(sopInstanceUID: string) {
    const instanceId = resolveInstanceId(sopInstanceUID);
    if (!instanceId) return null;
    await ensureConnected();
    const client = requireOrthanc();
    return client.getInstanceSimplifiedTags(instanceId);
  }

  // ==============================================================
  //  CLEANUP
  // ==============================================================

  function $reset() {
    config.value = null;
    _orthanc = undefined;
    _dicomWeb = undefined;
    _connectPromise = undefined;
    connectionState.value = 'idle';
    connectionError.value = null;
    studies.value = [];
    operations.value = {};
    studyIdByUID.value.clear();
    studyUIDById.value.clear();
    seriesIdByUID.value.clear();
    seriesUIDById.value.clear();
    instanceIdByUID.value.clear();
    instanceUIDById.value.clear();
    statistics.value = null;
    stopStatusPolling();
    statusMessage.value = '';
    stopStudyPolling();
    stopSeriesPolling();
  }

  // ==============================================================
  //  PUBLIC API
  // ==============================================================

  return {
    config,
    useCompression,
    connectionState,
    connectionError,
    isConnected,
    connect,
    getConfig,
    operations,
    activeOperations,
    clearOperation,

    resolveStudyId,
    resolveStudyUID,
    resolveSeriesId,
    resolveSeriesUID,
    resolveInstanceId,
    resolveInstanceUID,

    studies,
    fetchStudies,
    statistics,
    fetchStatistics,

    studySeries,
    selectedStudyOrthancID,
    fetchStudySeries,

    fetchOcg19Metadata,
    rebuildOcg19Metadata,
    rebuildAllOcg19Metadata,

    systemInfo,
    fetchSystemInfo,

    fetchFrameRaw,
    fetchFrameRawDicomWeb,
    fetchFrameNumpy,

    deleteStudies,

    uploadFiles,

    fetchPaletteLut,

    fetchThumbnail,
    fetchThumbnails,

    statusMessage,
    lastChange,
    setStatusMessage,
    startStatusPolling,
    stopStatusPolling,

    getInstanceSimplifiedTags,

    startStudyPolling,
    stopStudyPolling,
    startSeriesPolling,
    stopSeriesPolling,
    $reset,
  };
});
